import { FleetVehicle } from "services/api/fleet/vehicles/types";
import { VehicleListing, MarketplaceDetails } from "services/api/sales/listings/types";
import { SalesChannel } from "services/api/sales/channels/types";
import { ListingDefaultsFormValues } from "services/api/settings/listings/types";
import {
  ListingEditFormValues,
  MarketplaceData,
  MarketplaceVehicleSpecs,
  MarketplaceContact,
  MarketplaceExtraData,
  MarketplaceLocation,
  MarketplacePayment,
  WizardPictureItem,
} from "../types";
import { initialChannelDataEntry, initialMarketplaceData } from "../consts";
import {
  mapBodyShapeCode,
  mapDriveTypeCode,
  mapFuelTypeCode,
  mapTransmissionCode,
} from "./codeMaps";

const str = (v: unknown): string => (v == null ? "" : String(v));

/*===========================================================================
  PREFILL IZ VOZILA (create mod) — FleetVehicle → marketplace form sekcije
===========================================================================*/

// Parse "205/55 R16" / "205/55R16" / "205-55-R16" → { width, height, diameter }.
const parseTire = (tire: string | null | undefined) => {
  if (!tire) return null;
  const m = tire.match(/(\d{2,3})\s*[\/\-]\s*(\d{1,3})\s*[\/\-\s]*R?(\d{1,2})/i);
  if (!m) return null;
  return { width: m[1], height: m[2], diameter: m[3] };
};

export const prefillSpecsFromVehicle = (vehicle: FleetVehicle | null | undefined): Partial<MarketplaceVehicleSpecs> => {
  if (!vehicle) return {};
  const v = vehicle as any;
  const model = v.vehicleModel ?? {};
  const type = v.vehicleType ?? {};

  // Gume — preuzimamo iz osovine[0] ako je dostupno (CVH `Gume1`-style),
  // alternativno iz `type.tires` jednog stringa.
  // Osovina drži dimenzije gume: width / ratio (visina) / diameter.
  const tireFromAxle = Array.isArray(type.axles) && type.axles.length > 0
    ? {
        width: str(type.axles[0]?.width),
        height: str(type.axles[0]?.ratio),
        diameter: str(type.axles[0]?.diameter),
      }
    : null;
  const tireFromString = parseTire(type.tires ?? type.gume ?? null);

  return {
    manufacturingYear: str(model.year ?? model.yearFrom),
    manufacturerId: str(v.vehicleManufacturer?.id),
    modelId: str(model.id),
    typeName: str(type.name),
    // Code mapping katalog → marketplace dropdown (vidi codeMaps.ts).
    fuelTypeCode: mapFuelTypeCode(type.fuelType?.id ?? type.fuelType?.code),
    driveTypeCode: mapDriveTypeCode(type.drivenWheels?.id ?? type.drivenWheels?.code),
    transmissionCode: mapTransmissionCode(type.transmission?.id ?? type.transmission?.code),
    bodyShapeCode: mapBodyShapeCode(type.bodyCode),
    doors: str(type.numberOfDoors),
    powerKw: str(type.maximumPowerKw),
    displacementCm3: str(type.cylinderCapacity),
    modelYear: str(model.year ?? model.yearFrom),
    seats: str(type.seats),
    colorId: str(v.colorName ?? v.vehicleColorId),
    kmTraveled: str(v.kmTraveled),
    tireWidth: str(tireFromAxle?.width ?? tireFromString?.width ?? ""),
    tireHeight: str(tireFromAxle?.height ?? tireFromString?.height ?? ""),
    tireDiameter: str(tireFromAxle?.diameter ?? tireFromString?.diameter ?? ""),
    // Sva fleet vozila su rabljena — heuristika koja štedi userov klik.
    vehicleConditionCode: "used",
  };
};

// Dimenzije gume iz aktivnog tire seta vozila (fleet tires) — to je stvarni
// izvor guma na vozilu, prioritetan nad kataloškim osovinama. Uzima prvi tire
// iz seta (width / height / radius).
export const prefillTiresFromTireSet = (tireSet: any): Partial<MarketplaceVehicleSpecs> => {
  const tire = tireSet?.tires?.[0];
  if (!tire) return {};
  return {
    tireWidth: str(tire.width),
    tireHeight: str(tire.height),
    tireDiameter: str(tire.radius),
  };
};

export const prefillExtraFromVehicle = (vehicle: FleetVehicle | null | undefined): Partial<MarketplaceExtraData> => {
  if (!vehicle) return {};
  const v = vehicle as any;
  const type = v.vehicleType ?? {};

  // Godina prve registracije iz `firstRegistrationDate` — Croatia heuristika
  // (zakon kao da je registracija u RH, što je istina za većinu fleeta).
  let firstRegYear = "";
  if (v.firstRegistrationDate) {
    const d = new Date(v.firstRegistrationDate);
    if (!isNaN(d.getTime())) firstRegYear = String(d.getFullYear());
  }

  // Plin — `secondaryFuelType` truthy znači plinska instalacija.
  const secondary = String(type.secondaryFuelType ?? "").toLowerCase();
  const hasGas = secondary.includes("lpg") || secondary.includes("cng") || secondary.includes("plin");

  return {
    chassisNumber: str(v.vinNumber),
    co2Emission: str(type.co2),
    firstRegistrationYearRH: firstRegYear,
    inTrafficSinceYear: firstRegYear,
    hasGas,
  };
};

/*===========================================================================
  PREFILL IZ LISTINGA (edit mod) — VehicleListing → wizard form state
===========================================================================*/

// Pretvara MarketplaceDetails (BE response shape) u MarketplaceData (form
// state). Polje-po-polje mapiranje uz fallback na initial vrijednosti za
// nullable polja koja FE drži kao string-ove.
export const marketplaceDetailsToForm = (md: MarketplaceDetails | null): MarketplaceData => {
  if (!md) return initialMarketplaceData;
  const s = (v: string | number | null | undefined) => (v == null ? "" : String(v));
  return {
    specs: {
      manufacturingYear: s(md.manufacturingYear),
      manufacturerId: s(md.manufacturerId),
      modelId: s(md.modelId),
      typeName: s(md.typeName),
      fuelTypeCode: s(md.fuelTypeCode),
      driveTypeCode: s(md.driveTypeCode),
      doors: s(md.doors),
      powerKw: s(md.powerKw),
      transmissionCode: s(md.transmissionCode),
      bodyShapeCode: s(md.bodyShapeCode),
      displacementCm3: s(md.displacementCm3),
      modelYear: s(md.modelYear),
      seats: s(md.seats),
      suspensionCode: s(md.suspensionCode),
      colorId: s(md.colorId),
      metallic: !!md.metallic,
      tireWidth: s(md.tireWidth),
      tireHeight: s(md.tireHeight),
      tireDiameter: s(md.tireDiameter),
      kmTraveled: s(md.kmTraveled),
      vehicleConditionCode: s(md.vehicleConditionCode),
    },
    extra: {
      fuelConsumption: s(md.fuelConsumption),
      co2Emission: s(md.co2Emission),
      ecoCategoryCode: s(md.ecoCategoryCode),
      hasGas: !!md.hasGas,
      garaged: !!md.garaged,
      inTrafficSinceYear: s(md.inTrafficSinceYear),
      firstRegistrationYearRH: s(md.firstRegistrationYearRH),
      registeredUntil: s(md.registeredUntil),
      warrantyCode: s(md.warrantyCode),
      extendedWarranty: !!md.extendedWarranty,
      g1ClubWarranty: !!md.g1ClubWarranty,
      ownerCountCode: s(md.ownerCountCode),
      serviceBook: !!md.serviceBook,
      chassisNumber: s(md.chassisNumber),
      videoCallViewing: !!md.videoCallViewing,
      internalCode: s(md.internalCode),
    },
    equipment: {
      selected: md.equipment ?? {},
      airbagsCode: s(md.airbagsCode),
      airConditioningCode: s(md.airConditioningCode),
      carRadioCode: s(md.carRadioCode),
    },
    payment: {
      priceIncludesImport: (md.priceIncludesImport === "with" || md.priceIncludesImport === "without")
        ? md.priceIncludesImport
        : "",
      options: md.paymentOptions ?? {},
    },
    location: {
      countyCode: s(md.locationCountyCode),
      municipalityCode: s(md.locationMunicipalityCode),
      settlementCode: s(md.locationSettlementCode),
      manualAddress: s(md.locationManualAddress),
      display: md.locationDisplay === "exact" ? "exact" : "approximate",
    },
    contact: {
      contactUserIds: md.contactUserIds ?? [],
      // Stariji oglasi mogu imati više poslovnica; čarobnjak ih sada nosi jednu.
      locationOfficeIds: (md.locationOfficeIds ?? []).slice(0, 1),
      contactId: s(md.contactId),
    },
    youtubeUrl: s(md.youtubeUrl),
  };
};

// Konstruira inicijalne form values iz postojećeg listinga. Marketplace kanal
// daje selected slike (samo one koje su trenutno objavljene). Aktivni kanali
// određuju `selectedChannelIds`. ChannelData se popunjava override-ima.
export const buildEditFormValues = (
  listing: VehicleListing,
  marketplaceChannelId: string | null,
  channels: SalesChannel[],
  vehicle: FleetVehicle | null,
  pictures: WizardPictureItem[]
): ListingEditFormValues => {
  const s = (v: string | number | null | undefined) => (v == null ? "" : String(v));

  const selectedChannelIds = listing.channels
    .filter((ch) => ch.isActive)
    .map((ch) => ch.salesChannelId);

  // marketplaceChannelId je SalesChannel.id (iz kataloga). Match na
  // listing-channel preko salesChannelId, s fallbackom na code.
  const marketplaceChannel = listing.channels.find((ch) => ch.salesChannelId === marketplaceChannelId)
    ?? listing.channels.find((ch) => ch.salesChannelCode === "marketplace");

  // Marketplace je obavezan kanal — uvijek mora biti označen čak i ako je na
  // BE-u trenutno inactive (može se npr. dogoditi u starijim listingima ili
  // nakon ručnih intervencija).
  if (marketplaceChannel && !selectedChannelIds.includes(marketplaceChannel.salesChannelId)) {
    selectedChannelIds.push(marketplaceChannel.salesChannelId);
  }
  const selectedPictureUploadIds = (marketplaceChannel?.images ?? [])
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((img) => img.uploadId);

  const channelData: Record<string, typeof initialChannelDataEntry> = {};
  for (const ch of listing.channels) {
    channelData[ch.salesChannelId] = {
      titleOverride: s(ch.titleOverride),
      priceOverride: s(ch.priceOverride),
      descriptionOverride: s(ch.descriptionOverride),
    };
  }

  return {
    selectedChannelIds,
    baseTitle: s(listing.baseTitle),
    basePrice: s(listing.basePrice),
    baseDescription: s(listing.baseDescription),
    selectedPictureUploadIds,
    marketplaceData: marketplaceDetailsToForm(listing.marketplaceDetails),
    channelData,
    channels,
    vehicle,
    pictures,
    // Kod uređivanja postojećeg oglasa oprema je već potvrđena, pa se nemapirano s vozila ne
    // nameće ponovno.
    unmatchedEquipment: [],
    // Predlošci se nude samo pri unosu novog oglasa — postojeći tekst se ne pregazi.
    templates: [],
    templateId: "",
  };
};

/*===========================================================================
  PREFILL IZ POSTAVKI (tenant defaults) — ListingDefaults → wizard form
===========================================================================*/

export interface ListingDefaultsPrefill {
  location: Partial<MarketplaceLocation>;
  contact: Partial<MarketplaceContact>;
  payment: Partial<MarketplacePayment>;
  // warranty/video žive u MarketplaceExtraData
  extra: Partial<Pick<MarketplaceExtraData, "warrantyCode" | "extendedWarranty" | "g1ClubWarranty" | "videoCallViewing">>;
}

// Mapira tenant zadane postavke u dijelove marketplace form-state-a koji se
// mergeaju u CREATE bootstrapu. Sve je override-abilno po pojedinom oglasu.
export const prefillFromListingDefaults = (
  defaults: ListingDefaultsFormValues
): ListingDefaultsPrefill => ({
  // Lokacija se čita iz adrese prve odabrane poslovnice — ručnog unosa u
  // postavkama više nema, a oglasnici i dalje traže naselje i adresu.
  location: {
    countyCode: "",
    municipalityCode: "",
    settlementCode: defaults.offices[0]?.city ?? "",
    manualAddress: defaults.offices[0]?.address ?? "",
    display: defaults.locationDisplay,
  },
  contact: {
    // Kontakt iz postavki je jedna osoba — postavke ih smiju imati više, pa se uzima prva.
    contactUserIds: defaults.contactUserIds.slice(0, 1),
    // Oglas nosi jednu poslovnicu jer ona određuje adresu; postavke ih smiju imati više,
    // pa se uzima prva.
    locationOfficeIds: defaults.locationOfficeIds.slice(0, 1),
  },
  payment: {
    priceIncludesImport: defaults.priceIncludesImport,
    options: defaults.paymentOptions,
  },
  extra: {
    warrantyCode: defaults.warrantyCode,
    extendedWarranty: defaults.extendedWarranty,
    g1ClubWarranty: defaults.g1ClubWarranty,
    videoCallViewing: defaults.videoCallViewing,
  },
});
